import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DocumentDuplicateIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Modal, { ModalFooter } from '@/components/common/Modal';
import Badge from '@/components/common/Badge';
import { formatDate } from '@/utils/formatters';
import toast from 'react-hot-toast';
import {
  getDuplicateGroups,
  deleteDuplicates,
  bulkDeleteDuplicates,
} from '@/services/blogApi';
import type {
  DuplicateGroup,
  DuplicatePost,
  DuplicateDeletionRequest,
  DuplicateMatchType,
} from '@/types/blog';

const STATUS_BADGES: Record<string, { variant: 'gray' | 'info' | 'success' | 'warning' | 'danger'; label: string }> = {
  draft: { variant: 'gray', label: 'Draft' },
  in_review: { variant: 'warning', label: 'In Review' },
  published: { variant: 'success', label: 'Published' },
  archived: { variant: 'info', label: 'Archived' },
};

const MATCH_TYPE_LABELS: Record<DuplicateMatchType, { label: string; color: string }> = {
  primary_keyword: { label: 'Same Keyword', color: 'bg-red-100 text-red-800' },
  similar_title: { label: 'Similar Title', color: 'bg-orange-100 text-orange-800' },
  category_keywords: { label: 'Keyword Overlap', color: 'bg-yellow-100 text-yellow-800' },
};

export default function DuplicateManager() {
  const queryClient = useQueryClient();

  // State
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [currentGroup, setCurrentGroup] = useState<DuplicateGroup | null>(null);
  const [customSelections, setCustomSelections] = useState<Map<string, string>>(new Map()); // group_key -> keep_id

  // Fetch duplicate groups
  const { data: duplicateData, isLoading, refetch } = useQuery({
    queryKey: ['duplicateGroups'],
    queryFn: getDuplicateGroups,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteDuplicates,
    onSuccess: (result) => {
      if (result.success) {
        toast.success(`Deleted ${result.deleted_count} duplicate(s), kept "${result.kept_post.title}"`);
        queryClient.invalidateQueries({ queryKey: ['duplicateGroups'] });
        queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
        queryClient.invalidateQueries({ queryKey: ['blogStats'] });
      } else {
        toast.error(result.error || 'Failed to delete duplicates');
      }
      setShowConfirmModal(false);
      setCurrentGroup(null);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete duplicates');
    },
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: bulkDeleteDuplicates,
    onSuccess: (result) => {
      if (result.success) {
        toast.success(`Deleted ${result.total_deleted} duplicates across ${result.results.length} groups`);
      } else {
        const successCount = result.results.filter((r) => r.success).length;
        const failCount = result.results.filter((r) => !r.success).length;
        toast.error(`Completed with errors: ${successCount} succeeded, ${failCount} failed`);
      }
      queryClient.invalidateQueries({ queryKey: ['duplicateGroups'] });
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
      queryClient.invalidateQueries({ queryKey: ['blogStats'] });
      setShowBulkConfirmModal(false);
      setSelectedGroups(new Set());
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Bulk delete failed');
    },
  });

  // Toggle group expansion
  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  // Toggle group selection
  const toggleGroupSelection = (groupKey: string) => {
    const newSelected = new Set(selectedGroups);
    if (newSelected.has(groupKey)) {
      newSelected.delete(groupKey);
    } else {
      newSelected.add(groupKey);
    }
    setSelectedGroups(newSelected);
  };

  // Select/deselect all groups
  const toggleSelectAll = () => {
    if (selectedGroups.size === (duplicateData?.groups.length || 0)) {
      setSelectedGroups(new Set());
    } else {
      setSelectedGroups(new Set(duplicateData?.groups.map((g) => g.group_key) || []));
    }
  };

  // Handle single group delete
  const handleDeleteGroup = (group: DuplicateGroup) => {
    setCurrentGroup(group);
    setShowConfirmModal(true);
  };

  // Confirm single delete
  const confirmDelete = () => {
    if (!currentGroup) return;

    const keepId = customSelections.get(currentGroup.group_key) || currentGroup.recommended_keep_id;
    const deleteIds = currentGroup.posts.filter((p) => p.id !== keepId).map((p) => p.id);

    deleteMutation.mutate({
      keep_id: keepId,
      delete_ids: deleteIds,
      group_key: currentGroup.group_key,
    });
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (selectedGroups.size === 0) return;
    setShowBulkConfirmModal(true);
  };

  // Confirm bulk delete
  const confirmBulkDelete = () => {
    const requests: DuplicateDeletionRequest[] = [];

    for (const groupKey of selectedGroups) {
      const group = duplicateData?.groups.find((g) => g.group_key === groupKey);
      if (!group) continue;

      const keepId = customSelections.get(groupKey) || group.recommended_keep_id;
      requests.push({
        keep_id: keepId,
        delete_ids: group.posts.filter((p) => p.id !== keepId).map((p) => p.id),
        group_key: groupKey,
      });
    }

    bulkDeleteMutation.mutate(requests);
  };

  // Change which post to keep in a group
  const setKeepPost = (groupKey: string, postId: string) => {
    const newSelections = new Map(customSelections);
    newSelections.set(groupKey, postId);
    setCustomSelections(newSelections);
  };

  // Get the post that will be kept for a group
  const getKeptPostId = (group: DuplicateGroup) => {
    return customSelections.get(group.group_key) || group.recommended_keep_id;
  };

  // Count posts that will be deleted in selected groups
  const selectedDeleteCount = useMemo(() => {
    let count = 0;
    for (const groupKey of selectedGroups) {
      const group = duplicateData?.groups.find((g) => g.group_key === groupKey);
      if (group) {
        count += group.posts.length - 1; // Subtract 1 for the kept post
      }
    }
    return count;
  }, [selectedGroups, duplicateData]);

  // Render individual post in comparison view
  const renderPost = (post: DuplicatePost, group: DuplicateGroup) => {
    const keepId = getKeptPostId(group);
    const isKept = post.id === keepId;

    return (
      <div
        key={post.id}
        className={`p-4 rounded-lg border-2 transition-all ${
          isKept ? 'border-green-500 bg-green-50' : 'border-red-300 bg-red-50'
        }`}
      >
        <div className="flex items-start justify-between mb-2">
          <Badge variant={isKept ? 'success' : 'danger'}>{isKept ? 'KEEP' : 'DELETE'}</Badge>
          {!isKept && (
            <button
              onClick={() => setKeepPost(group.group_key, post.id)}
              className="text-sm text-blue-600 hover:underline"
            >
              Keep this instead
            </button>
          )}
        </div>

        <h4 className="font-medium text-gray-900 mb-1 line-clamp-2">{post.title}</h4>
        <p className="text-sm text-gray-500 mb-2 truncate">/{post.slug}</p>

        <div className="flex flex-wrap gap-2 text-xs text-gray-600">
          <span className="flex items-center gap-1">
            {post.featured_image_url ? (
              <>
                <PhotoIcon className="w-4 h-4 text-green-500" /> Has image
              </>
            ) : (
              <>
                <PhotoIcon className="w-4 h-4 text-gray-400" /> No image
              </>
            )}
          </span>
          <span>Created: {formatDate(post.created_at)}</span>
          <span>Views: {post.view_count}</span>
        </div>

        <div className="mt-2">
          <Badge variant={STATUS_BADGES[post.status]?.variant || 'gray'}>
            {STATUS_BADGES[post.status]?.label || post.status}
          </Badge>
        </div>

        {post.primary_keyword && (
          <p className="mt-2 text-xs text-gray-600">
            <span className="font-medium">Keyword:</span> {post.primary_keyword}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Duplicate Manager</h1>
          <p className="mt-1 text-sm text-gray-500">
            Review and clean up duplicate blog posts
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => refetch()}
            disabled={isLoading}
            leftIcon={<ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />}
          >
            Refresh
          </Button>
          {selectedGroups.size > 0 && (
            <Button
              variant="danger"
              onClick={handleBulkDelete}
              leftIcon={<TrashIcon className="w-4 h-4" />}
            >
              Delete Selected ({selectedDeleteCount} posts from {selectedGroups.size} groups)
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Duplicate Groups</p>
            <p className="text-2xl font-bold text-orange-600">
              {duplicateData?.total_groups || 0}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Total Duplicates to Remove</p>
            <p className="text-2xl font-bold text-red-600">
              {duplicateData?.total_duplicates || 0}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Selected for Deletion</p>
            <p className="text-2xl font-bold text-gray-600">{selectedDeleteCount}</p>
          </CardBody>
        </Card>
      </div>

      {/* Bulk Selection Controls */}
      {duplicateData && duplicateData.groups.length > 0 && (
        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
          <input
            type="checkbox"
            checked={selectedGroups.size === duplicateData.groups.length}
            onChange={toggleSelectAll}
            className="rounded border-gray-300"
            aria-label="Select all duplicate groups"
          />
          <span className="text-sm text-gray-700">
            {selectedGroups.size === duplicateData.groups.length
              ? 'Deselect all groups'
              : 'Select all groups'}
          </span>
          {selectedGroups.size > 0 && (
            <span className="text-sm text-gray-500">
              ({selectedGroups.size} of {duplicateData.groups.length} selected)
            </span>
          )}
        </div>
      )}

      {/* Duplicate Groups List */}
      {isLoading ? (
        <Card>
          <CardBody className="py-12 text-center">
            <div className="flex items-center justify-center gap-2 text-gray-500">
              <div className="w-5 h-5 border-2 border-brand border-t-transparent rounded-full animate-spin" />
              <span>Scanning for duplicates...</span>
            </div>
          </CardBody>
        </Card>
      ) : duplicateData?.groups.length === 0 ? (
        <Card>
          <CardBody className="py-12 text-center">
            <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No Duplicates Found</h3>
            <p className="text-gray-500">Your blog posts are unique!</p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-4">
          {duplicateData?.groups.map((group) => (
            <Card key={group.group_key}>
              <CardBody>
                {/* Group Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <input
                      type="checkbox"
                      checked={selectedGroups.has(group.group_key)}
                      onChange={() => toggleGroupSelection(group.group_key)}
                      className="rounded border-gray-300"
                      aria-label={`Select duplicate group ${group.group_key}`}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <DocumentDuplicateIcon className="w-5 h-5 text-orange-500" />
                        <span className="font-medium">
                          {group.posts.length} posts
                          {group.match_type === 'primary_keyword' && (
                            <> with keyword "{group.group_key}"</>
                          )}
                          {group.match_type === 'similar_title' && <> with similar titles</>}
                          {group.match_type === 'category_keywords' && <> with overlapping keywords</>}
                        </span>
                      </div>
                      <span
                        className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${
                          MATCH_TYPE_LABELS[group.match_type].color
                        }`}
                      >
                        {MATCH_TYPE_LABELS[group.match_type].label}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteGroup(group)}
                      leftIcon={<TrashIcon className="w-4 h-4" />}
                    >
                      Clean Up
                    </Button>
                    <button
                      onClick={() => toggleGroup(group.group_key)}
                      className="p-2 hover:bg-gray-100 rounded transition-colors"
                    >
                      {expandedGroups.has(group.group_key) ? (
                        <ChevronUpIcon className="w-5 h-5" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Expanded: Side-by-side comparison */}
                {expandedGroups.has(group.group_key) && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {group.posts.map((post) => renderPost(post, group))}
                  </div>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Single Delete Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirm Duplicate Cleanup"
        size="lg"
      >
        {currentGroup && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-800">Review before deleting</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    This will permanently delete {currentGroup.posts.length - 1} post(s).
                  </p>
                </div>
              </div>
            </div>

            <div className="border rounded-lg divide-y overflow-hidden">
              <div className="p-3 bg-green-50">
                <span className="text-sm font-medium text-green-800">KEEPING:</span>
                <p className="text-sm text-green-700 mt-1">
                  {currentGroup.posts.find((p) => p.id === getKeptPostId(currentGroup))?.title}
                </p>
              </div>
              <div className="p-3 bg-red-50">
                <span className="text-sm font-medium text-red-800">DELETING:</span>
                <ul className="text-sm text-red-700 list-disc list-inside mt-1">
                  {currentGroup.posts
                    .filter((p) => p.id !== getKeptPostId(currentGroup))
                    .map((p) => (
                      <li key={p.id}>{p.title}</li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        )}
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            loading={deleteMutation.isPending}
          >
            Delete Duplicates
          </Button>
        </ModalFooter>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        isOpen={showBulkConfirmModal}
        onClose={() => setShowBulkConfirmModal(false)}
        title="Confirm Bulk Duplicate Cleanup"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            You are about to clean up <strong>{selectedGroups.size}</strong> duplicate groups.
          </p>
          <p className="text-gray-600">
            This will delete <strong>{selectedDeleteCount}</strong> posts, keeping the recommended
            post from each group.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-700">
              This action cannot be undone. Make sure you have reviewed the selections.
            </p>
          </div>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowBulkConfirmModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmBulkDelete}
            loading={bulkDeleteMutation.isPending}
          >
            Delete All Duplicates ({selectedDeleteCount} posts)
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
